/**
 * @engine/core - SpriteRendererStorage
 * 
 * Structure of Arrays (SoA) storage for sprite renderer data.
 * Optimized for batch rendering operations.
 * 
 * Layout:
 * - textureIds: Int32Array [texId0, texId1, ...] asset handle
 * - regions: Float32Array [u0,v0,u1,v1, ...] UV coordinates
 * - colors: Uint32Array [rgba0, rgba1, ...] packed color
 * - pivots: Float32Array [px0,py0, ...] normalized pivot point
 * - sizes: Float32Array [w0,h0, ...] sprite size
 * - sortingLayers: Int16Array [layer0, ...] rendering order
 * - flags: Uint8Array [enabled, flipX, flipY bits]
 */

// Flag bits
const FLAG_ENABLED = 0x01;
const FLAG_FLIP_X = 0x02;
const FLAG_FLIP_Y = 0x04;
const FLAG_VISIBLE = 0x08;

// Strides
const REGION_STRIDE = 4; // u0,v0,u1,v1
const PIVOT_STRIDE = 2;  // px,py
const SIZE_STRIDE = 2;   // w,h

// Safe array access - storage guarantees valid indices
function get<T>(arr: ArrayLike<T>, i: number): T { return arr[i] as T; }

/**
 * Sprite renderer slot handle
 */
export type SpriteHandle = number;

/**
 * Invalid handle constant
 */
export const INVALID_SPRITE_HANDLE: SpriteHandle = -1;

/**
 * Default color (white, fully opaque)
 */
export const DEFAULT_COLOR = 0xFFFFFFFF;

/**
 * Sprite renderer data for bulk operations
 */
export interface SpriteRendererData {
  textureId: number;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  colorPacked: number;
  pivotX: number;
  pivotY: number;
  width: number;
  height: number;
  sortingLayer: number;
  orderInLayer: number;
  flipX: boolean;
  flipY: boolean;
  enabled: boolean;
}

/**
 * Render batch entry (for sorting)
 */
export interface SpriteBatchEntry {
  handle: SpriteHandle;
  textureId: number;
  sortingLayer: number;
  orderInLayer: number;
  zIndex: number;
}

/**
 * SpriteRendererStorage configuration
 */
export interface SpriteRendererStorageConfig {
  initialCapacity?: number;
  growFactor?: number;
}

/**
 * SpriteRendererStorage - SoA storage for sprite renderers
 * 
 * @example
 * ```typescript
 * const storage = new SpriteRendererStorage({ initialCapacity: 1000 });
 * 
 * const handle = storage.allocate();
 * storage.setTexture(handle, textureAssetId);
 * storage.setRegion(handle, 0, 0, 1, 1); // Full texture
 * storage.setColor(handle, 0xFFFFFFFF); // White
 * 
 * // Get visible sprites for rendering
 * const batch = storage.getBatchEntries(transformStorage);
 * ```
 */
export class SpriteRendererStorage {
  // Texture reference (asset ID)
  private _textureIds!: Int32Array;
  
  // UV region (normalized)
  private _regions!: Float32Array;
  
  // Color (packed RGBA)
  private _colors!: Uint32Array;
  
  // Pivot (normalized 0-1, default 0.5,0.5 = center)
  private _pivots!: Float32Array;
  
  // Size override (0 = use texture size)
  private _sizes!: Float32Array;
  
  // Sorting layer
  private _sortingLayers!: Int16Array;
  
  // Order within sorting layer
  private _orderInLayers!: Int16Array;
  
  // Flags (enabled, flip, etc.)
  private _flags!: Uint8Array;
  
  // Allocation tracking
  private _capacity: number = 0;
  private _count: number = 0;
  private _freeList: number[] = [];
  private _growFactor: number;
  
  // Dirty tracking for batch rebuild
  private _batchDirty: boolean = true;
  private _sortedBatch: SpriteBatchEntry[] = [];
  
  constructor(config: SpriteRendererStorageConfig = {}) {
    const initialCapacity = config.initialCapacity ?? 256;
    this._growFactor = config.growFactor ?? 2;
    
    this.resize(initialCapacity);
  }
  
  // ===========================================================================
  // CAPACITY
  // ===========================================================================
  
  get capacity(): number {
    return this._capacity;
  }
  
  get count(): number {
    return this._count;
  }
  
  resize(newCapacity: number): void {
    if (newCapacity <= this._capacity) return;
    
    // Allocate new arrays
    const newTextureIds = new Int32Array(newCapacity);
    const newRegions = new Float32Array(newCapacity * REGION_STRIDE);
    const newColors = new Uint32Array(newCapacity);
    const newPivots = new Float32Array(newCapacity * PIVOT_STRIDE);
    const newSizes = new Float32Array(newCapacity * SIZE_STRIDE);
    const newSortingLayers = new Int16Array(newCapacity);
    const newOrderInLayers = new Int16Array(newCapacity);
    const newFlags = new Uint8Array(newCapacity);
    
    // Copy existing data
    if (this._capacity > 0) {
      newTextureIds.set(this._textureIds);
      newRegions.set(this._regions);
      newColors.set(this._colors);
      newPivots.set(this._pivots);
      newSizes.set(this._sizes);
      newSortingLayers.set(this._sortingLayers);
      newOrderInLayers.set(this._orderInLayers);
      newFlags.set(this._flags);
    }
    
    // Initialize new slots
    for (let i = this._capacity; i < newCapacity; i++) {
      newTextureIds[i] = -1;
      newRegions[i * REGION_STRIDE + 0] = 0; // u0
      newRegions[i * REGION_STRIDE + 1] = 0; // v0
      newRegions[i * REGION_STRIDE + 2] = 1; // u1
      newRegions[i * REGION_STRIDE + 3] = 1; // v1
      newColors[i] = DEFAULT_COLOR;
      newPivots[i * PIVOT_STRIDE + 0] = 0.5; // centered pivot
      newPivots[i * PIVOT_STRIDE + 1] = 0.5;
      newSizes[i * SIZE_STRIDE + 0] = 0; // 0 = auto from texture
      newSizes[i * SIZE_STRIDE + 1] = 0;
      newSortingLayers[i] = 0;
      newOrderInLayers[i] = 0;
      newFlags[i] = FLAG_ENABLED | FLAG_VISIBLE;
    }
    
    this._textureIds = newTextureIds;
    this._regions = newRegions;
    this._colors = newColors;
    this._pivots = newPivots;
    this._sizes = newSizes;
    this._sortingLayers = newSortingLayers;
    this._orderInLayers = newOrderInLayers;
    this._flags = newFlags;
    this._capacity = newCapacity;
  }
  
  // ===========================================================================
  // ALLOCATION
  // ===========================================================================
  
  allocate(): SpriteHandle {
    // Try to use free slot
    if (this._freeList.length > 0) {
      const handle = this._freeList.pop()!;
      this.resetSlot(handle);
      this._count++;
      this._batchDirty = true;
      return handle;
    }
    
    // Need more capacity?
    if (this._count >= this._capacity) {
      this.resize(Math.ceil(this._capacity * this._growFactor));
    }
    
    const handle = this._count;
    this.resetSlot(handle);
    this._count++;
    this._batchDirty = true;
    return handle;
  }
  
  free(handle: SpriteHandle): void {
    if (!this.isValid(handle)) return;
    
    this._flags[handle] = 0; // Clear all flags (marks as dealloc)
    this._textureIds[handle] = -1;
    this._freeList.push(handle);
    this._count--;
    this._batchDirty = true;
  }
  
  isValid(handle: SpriteHandle): boolean {
    return handle >= 0 && handle < this._capacity && this._flags[handle] !== 0;
  }
  
  private resetSlot(handle: SpriteHandle): void {
    this._textureIds[handle] = -1;
    
    const ri = handle * REGION_STRIDE;
    this._regions[ri + 0] = 0;
    this._regions[ri + 1] = 0;
    this._regions[ri + 2] = 1;
    this._regions[ri + 3] = 1;
    
    this._colors[handle] = DEFAULT_COLOR;
    
    const pi = handle * PIVOT_STRIDE;
    this._pivots[pi + 0] = 0.5;
    this._pivots[pi + 1] = 0.5;
    
    const si = handle * SIZE_STRIDE;
    this._sizes[si + 0] = 0;
    this._sizes[si + 1] = 0;
    
    this._sortingLayers[handle] = 0;
    this._orderInLayers[handle] = 0;
    this._flags[handle] = FLAG_ENABLED | FLAG_VISIBLE;
  }
  
  // ===========================================================================
  // TEXTURE
  // ===========================================================================
  
  getTextureId(handle: SpriteHandle): number {
    return get(this._textureIds, handle);
  }
  
  setTextureId(handle: SpriteHandle, textureId: number): void {
    this._textureIds[handle] = textureId;
    this._batchDirty = true;
  }
  
  // ===========================================================================
  // UV REGION
  // ===========================================================================
  
  getRegionU0(handle: SpriteHandle): number {
    return get(this._regions, handle * REGION_STRIDE + 0);
  }
  
  getRegionV0(handle: SpriteHandle): number {
    return get(this._regions, handle * REGION_STRIDE + 1);
  }
  
  getRegionU1(handle: SpriteHandle): number {
    return get(this._regions, handle * REGION_STRIDE + 2);
  }
  
  getRegionV1(handle: SpriteHandle): number {
    return get(this._regions, handle * REGION_STRIDE + 3);
  }
  
  setRegion(handle: SpriteHandle, u0: number, v0: number, u1: number, v1: number): void {
    const i = handle * REGION_STRIDE;
    this._regions[i + 0] = u0;
    this._regions[i + 1] = v0;
    this._regions[i + 2] = u1;
    this._regions[i + 3] = v1;
  }
  
  // ===========================================================================
  // COLOR
  // ===========================================================================
  
  getColor(handle: SpriteHandle): number {
    return get(this._colors, handle);
  }
  
  setColor(handle: SpriteHandle, packedColor: number): void {
    this._colors[handle] = packedColor;
  }
  
  setColorRGBA(handle: SpriteHandle, r: number, g: number, b: number, a: number): void {
    this._colors[handle] = 
      ((a & 0xFF) << 24) |
      ((b & 0xFF) << 16) |
      ((g & 0xFF) << 8) |
      (r & 0xFF);
  }
  
  // ===========================================================================
  // PIVOT
  // ===========================================================================
  
  getPivotX(handle: SpriteHandle): number {
    return get(this._pivots, handle * PIVOT_STRIDE + 0);
  }
  
  getPivotY(handle: SpriteHandle): number {
    return get(this._pivots, handle * PIVOT_STRIDE + 1);
  }
  
  setPivot(handle: SpriteHandle, x: number, y: number): void {
    const i = handle * PIVOT_STRIDE;
    this._pivots[i + 0] = x;
    this._pivots[i + 1] = y;
  }
  
  // ===========================================================================
  // SIZE
  // ===========================================================================
  
  getSizeWidth(handle: SpriteHandle): number {
    return get(this._sizes, handle * SIZE_STRIDE + 0);
  }
  
  getSizeHeight(handle: SpriteHandle): number {
    return get(this._sizes, handle * SIZE_STRIDE + 1);
  }
  
  setSize(handle: SpriteHandle, width: number, height: number): void {
    const i = handle * SIZE_STRIDE;
    this._sizes[i + 0] = width;
    this._sizes[i + 1] = height;
  }
  
  // ===========================================================================
  // SORTING
  // ===========================================================================
  
  getSortingLayer(handle: SpriteHandle): number {
    return get(this._sortingLayers, handle);
  }
  
  setSortingLayer(handle: SpriteHandle, layer: number): void {
    this._sortingLayers[handle] = layer;
    this._batchDirty = true;
  }
  
  getOrderInLayer(handle: SpriteHandle): number {
    return get(this._orderInLayers, handle);
  }
  
  setOrderInLayer(handle: SpriteHandle, order: number): void {
    this._orderInLayers[handle] = order;
    this._batchDirty = true;
  }
  
  // ===========================================================================
  // FLAGS
  // ===========================================================================
  
  isEnabled(handle: SpriteHandle): boolean {
    return (get(this._flags, handle) & FLAG_ENABLED) !== 0;
  }
  
  setEnabled(handle: SpriteHandle, enabled: boolean): void {
    if (enabled) {
      this._flags[handle] |= FLAG_ENABLED;
    } else {
      this._flags[handle] &= ~FLAG_ENABLED;
    }
    this._batchDirty = true;
  }
  
  isFlipX(handle: SpriteHandle): boolean {
    return (get(this._flags, handle) & FLAG_FLIP_X) !== 0;
  }
  
  setFlipX(handle: SpriteHandle, flip: boolean): void {
    if (flip) {
      this._flags[handle] |= FLAG_FLIP_X;
    } else {
      this._flags[handle] &= ~FLAG_FLIP_X;
    }
  }
  
  isFlipY(handle: SpriteHandle): boolean {
    return (get(this._flags, handle) & FLAG_FLIP_Y) !== 0;
  }
  
  setFlipY(handle: SpriteHandle, flip: boolean): void {
    if (flip) {
      this._flags[handle] |= FLAG_FLIP_Y;
    } else {
      this._flags[handle] &= ~FLAG_FLIP_Y;
    }
  }
  
  // ===========================================================================
  // BATCH QUERY
  // ===========================================================================
  
  /**
   * Get sprite data for rendering (for single sprite)
   */
  getData(handle: SpriteHandle): SpriteRendererData {
    const ri = handle * REGION_STRIDE;
    const pi = handle * PIVOT_STRIDE;
    const si = handle * SIZE_STRIDE;
    
    return {
      textureId: get(this._textureIds, handle),
      u0: get(this._regions, ri + 0),
      v0: get(this._regions, ri + 1),
      u1: get(this._regions, ri + 2),
      v1: get(this._regions, ri + 3),
      colorPacked: get(this._colors, handle),
      pivotX: get(this._pivots, pi + 0),
      pivotY: get(this._pivots, pi + 1),
      width: get(this._sizes, si + 0),
      height: get(this._sizes, si + 1),
      sortingLayer: get(this._sortingLayers, handle),
      orderInLayer: get(this._orderInLayers, handle),
      flipX: this.isFlipX(handle),
      flipY: this.isFlipY(handle),
      enabled: this.isEnabled(handle),
    };
  }
  
  /**
   * Get raw arrays for direct GPU upload (advanced)
   */
  getRawArrays() {
    return {
      textureIds: this._textureIds,
      regions: this._regions,
      colors: this._colors,
      pivots: this._pivots,
      sizes: this._sizes,
      sortingLayers: this._sortingLayers,
      orderInLayers: this._orderInLayers,
      flags: this._flags,
    };
  }
  
  /**
   * Mark batch as dirty (call when transform changes)
   */
  markBatchDirty(): void {
    this._batchDirty = true;
  }
  
  /**
   * Get sorted batch entries for rendering
   * Sorted by: sorting layer -> order in layer -> z coordinate -> texture (for batching)
   */
  getSortedBatch(getZIndex: (handle: SpriteHandle) => number): SpriteBatchEntry[] {
    // Rebuild batch if dirty
    if (this._batchDirty) {
      this._sortedBatch = [];
      
      for (let handle = 0; handle < this._capacity; handle++) {
        if (!this.isValid(handle) || !this.isEnabled(handle)) continue;
        
        this._sortedBatch.push({
          handle,
          textureId: get(this._textureIds, handle),
          sortingLayer: get(this._sortingLayers, handle),
          orderInLayer: get(this._orderInLayers, handle),
          zIndex: getZIndex(handle),
        });
      }
      
      // Sort: sortingLayer (asc) -> orderInLayer (asc) -> zIndex (asc) -> textureId (group for batching)
      this._sortedBatch.sort((a, b) => {
        if (a.sortingLayer !== b.sortingLayer) return a.sortingLayer - b.sortingLayer;
        if (a.orderInLayer !== b.orderInLayer) return a.orderInLayer - b.orderInLayer;
        if (a.zIndex !== b.zIndex) return a.zIndex - b.zIndex;
        return a.textureId - b.textureId;
      });
      
      this._batchDirty = false;
    }
    
    return this._sortedBatch;
  }
  
  /**
   * Clear all sprites
   */
  clear(): void {
    for (let i = 0; i < this._capacity; i++) {
      this._flags[i] = 0;
      this._textureIds[i] = -1;
    }
    this._freeList = [];
    this._count = 0;
    this._batchDirty = true;
    this._sortedBatch = [];
  }
}

// ===========================================================================
// SINGLETON
// ===========================================================================

let _storageInstance: SpriteRendererStorage | null = null;

export function getSpriteRendererStorage(): SpriteRendererStorage {
  if (!_storageInstance) {
    _storageInstance = new SpriteRendererStorage({ initialCapacity: 1024 });
  }
  return _storageInstance;
}

export function resetSpriteRendererStorage(): void {
  _storageInstance = null;
}
